import React, { useState } from 'react';
import { Button, List as MuiList, Stack } from '@mui/material';

import { ContentLayout } from 'components/Layout';
import { BreadCrumbs } from 'components/Breadcrumbs';
import { Loader } from 'components/Loader';
import { NotFound } from 'components/NotFound/NotFound';
import { Pagination } from 'components/Pagination';
import { usePageSize } from 'hook/usePageSize';
import { paths } from 'routes';

import { useNotifications } from '../api/getNotifications';
import { useUnreadNotificationsCount } from '../api/getUnreadCount';
import { useMarkAllNotificationsRead } from '../api/markRead';
import { NotificationItem } from '../components/NotificationItem';

export const NotificationsList = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize();
  const { data, isLoading, isError } = useNotifications({ page: page - 1, limit: pageSize });
  const { data: unreadCount = 0 } = useUnreadNotificationsCount();
  const { mutate: markAllRead, isLoading: isMarkingAllRead } = useMarkAllNotificationsRead();

  const notifications = data?.notifications ?? [];
  const total = data?.total ?? 0;

  return (
    <ContentLayout>
      <Stack height={1}>
        <BreadCrumbs
          items={[{ name: '', to: paths.notifications }, { name: `Notifications${total > 0 ? ` (${total})` : ''}` }]}
        />

        <Stack px={4} py={2} bgcolor="background.paper" borderTop="1px solid" borderColor="border.main" height="100%">
          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="outlined"
              size="small"
              onClick={() => markAllRead()}
              disabled={unreadCount === 0 || isMarkingAllRead}
            >
              Mark all as read
            </Button>
          </Stack>

          {isLoading ? (
            <Loader />
          ) : isError ? (
            <NotFound wrapperSx={{ mt: 19 }} title="Failed to load notifications" caption="Please try again later." />
          ) : notifications.length === 0 ? (
            <NotFound wrapperSx={{ mt: 19 }} title="You have no notifications" />
          ) : (
            <>
              <MuiList
                disablePadding
                sx={{ flex: 1, mt: 2, borderTop: '1px solid', borderColor: 'border.main' }}
              >
                {notifications.map((notification) => (
                  <NotificationItem key={notification.notification_id} notification={notification} />
                ))}
              </MuiList>
              <Stack direction="row" justifyContent="center">
                <Pagination
                  page={page}
                  handlePageChange={(_, newPage) => setPage(newPage)}
                  itemCount={total}
                  itemsPerPage={pageSize}
                  onItemsPerPageChange={setPageSize}
                />
              </Stack>
            </>
          )}
        </Stack>
      </Stack>
    </ContentLayout>
  );
};
